import { classroomCollectionKey } from "@/entities/classroom";
import {
  schoolCollectionKey,
  schoolConfigCollectionKey
} from "@/entities/school";
import { RoomSummaryWithRunningRegistrants } from "@/entities/room";
import { RunningTimer } from "@/entities/learning";
import {
  Student,
  StudentGrade,
  studentCollectionKey,
  StudentConfig,
  studentConfigCollectionKey,
  StudentSecret,
  StudentSecretData,
  StudentProperty,
  studentSecretCollectionKey,
  convertToStudent,
  convertToStudentConfig,
  convertToStudentSecret,
  elementarySchoolGrades,
  juniorHighSchoolGrades,
  highSchoolGrades
} from "@/entities/student";
import { SchoolAiUsageLimitScope } from "@/entities/school";
import {
  deleteRegularlyReservation,
  deleteReservation,
  getRegularlyReservationsOf,
  getReservationsOf
} from "@/api/reservation";
import firebase from "firebase/app";
import "firebase/firestore";
import "firebase/functions";
import { getSchoolConfig, getSchoolIdOfStudent } from "./school";
import store from "@/store";
import { chunkArray, MAX_BATCH_SIZE } from "@/utils/array";

export async function fetchStudentConfig(
  ref: firebase.firestore.DocumentReference
): Promise<StudentConfig | null> {
  const snapshot = await ref.get();
  const data = snapshot.data();
  if (!snapshot.exists || !data) {
    return null;
  }
  return convertToStudentConfig(data, snapshot.ref);
}

export async function fetchStudentSecrets(): Promise<StudentSecret[]> {
  const snapshot = await firebase
    .firestore()
    .collectionGroup(studentSecretCollectionKey)
    .get();
  if (snapshot.empty) {
    return [];
  }

  return snapshot.docs.map(doc => convertToStudentSecret(doc.data(), doc.ref));
}

export async function createOrUpdateStudentScret(
  ref: firebase.firestore.DocumentReference,
  data: StudentSecretData
): Promise<void> {
  const snapshot = await ref.get();
  if (!snapshot.exists) {
    await ref.set(data);
  } else {
    await ref.update(data);
  }
}

export async function getStudents(): Promise<Student[]> {
  const db = firebase.firestore();

  const studentsSnapshot = await db.collectionGroup(studentCollectionKey).get();
  if (studentsSnapshot.empty) {
    return [];
  }

  return studentsSnapshot.docs.map(doc =>
    convertToStudent(doc.data(), doc.ref)
  );
}

export async function getStudentsLazily(
  {
    step
  }: {
    step: number;
  } = { step: 100 }
): Promise<Student[]> {
  if (step < 1) throw new Error("Invalid step value");

  const db = firebase.firestore();

  const students: Student[] = [];
  let lastDoc: firebase.firestore.QueryDocumentSnapshot | null = null;
  let hasMore = true;

  while (hasMore) {
    if (store.state.cancelStudentFetch) {
      break;
    }

    let query = db
      .collectionGroup(studentCollectionKey)
      .orderBy("id")
      .limit(step);

    if (lastDoc) {
      query = query.startAfter(lastDoc);
    }

    const snapshot = await query.get();

    if (store.state.cancelStudentFetch) {
      break;
    }

    if (snapshot.empty || snapshot.docs.length === 0) {
      hasMore = false;
      break;
    }

    students.push(
      ...snapshot.docs.map(doc => convertToStudent(doc.data(), doc.ref))
    );

    if (snapshot.docs.length < step) {
      hasMore = false;
    } else {
      lastDoc = snapshot.docs[snapshot.docs.length - 1];
    }
  }

  return students;
}

export async function getStudentsOfClassroom(
  classroom: firebase.firestore.DocumentReference
): Promise<Student[]> {
  const studentsSnapshot = await classroom
    .collection(studentCollectionKey)
    .get();
  if (studentsSnapshot.empty) {
    return [];
  }

  return studentsSnapshot.docs.map(doc =>
    convertToStudent(doc.data(), doc.ref)
  );
}

export async function getStudentsOfClassroomLazily(
  classroom: firebase.firestore.DocumentReference,
  {
    step
  }: {
    step: number;
  } = { step: 100 }
): Promise<Student[]> {
  if (step < 1) throw new Error("Invalid step value");

  const students: Student[] = [];
  let lastDoc: firebase.firestore.QueryDocumentSnapshot | null = null;
  let hasMore = true;

  while (hasMore) {
    if (store.state.cancelStudentFetch) {
      break;
    }

    let query = classroom
      .collection(studentCollectionKey)
      .orderBy("id")
      .limit(step);

    if (lastDoc) {
      query = query.startAfter(lastDoc);
    }

    const snapshot = await query.get();

    if (store.state.cancelStudentFetch) {
      break;
    }

    if (snapshot.empty || snapshot.docs.length === 0) {
      hasMore = false;
      break;
    }

    students.push(
      ...snapshot.docs.map(doc => convertToStudent(doc.data(), doc.ref))
    );

    if (snapshot.docs.length < step) {
      hasMore = false;
    } else {
      lastDoc = snapshot.docs[snapshot.docs.length - 1];
    }
  }

  return students;
}

export async function getStudentList(
  start: number,
  end: number,
  limit?: { limit: number; lastVisiblePath?: string | null } | null
): Promise<{ [key: string]: string | number | boolean }[]> {
  const callGetStudentList = firebase
    .app()
    .functions("asia-northeast1")
    .httpsCallable("get_student_list", { timeout: 540 * 1000 }); //生徒増加によるタイムアウトエラーを防ぐため
  const res = await callGetStudentList({ start, end, limit });
  return res.data.datas;
}

export async function getStudentCount(): Promise<{ studentCount: number }> {
  const callGetStudentCount = firebase
    .app()
    .functions("asia-northeast1")
    .httpsCallable("get_student_count", { timeout: 540 * 1000 });
  const res = await callGetStudentCount();
  return res.data;
}

export async function getStatusList({
  type,
  tutorDocId
}: {
  type: "admin" | "tutor";
  tutorDocId: string;
}): Promise<{
  timers: RunningTimer[];
  rooms: RoomSummaryWithRunningRegistrants[];
}> {
  const callGetStatusList = firebase
    .app()
    .functions("asia-northeast1")
    .httpsCallable("get_student_status_list", { timeout: 540 * 1000 }); //生徒増加によるタイムアウトエラーを防ぐため

  const { data } = await callGetStatusList({ type, tutorDocId });
  return data;
}

export type NewStudentData = {
  classroomRef: firebase.firestore.DocumentReference;
  id: string;
  loginId: string;
  name: string;
  grade: StudentGrade;
  classSchedules: string[];
  emailReceivers: string[];
  password: string;
  properties: StudentProperty[] | [];
  characteristic: string;
  gmail?: string;
  microsoftEmail?: string;
  isPromptCreationDisabled?: boolean;
  isStudentMessageSendingAllowed?: boolean;
};

export async function registerStudent(student: NewStudentData): Promise<void> {
  const school = student.classroomRef.parent.parent!.id;
  const classroom = student.classroomRef.id;
  const callRegisterStudent = firebase
    .app()
    .functions("asia-northeast1")
    .httpsCallable("register_student", { timeout: 540 * 1000 });
  const res = await callRegisterStudent({
    school,
    classroom,
    student: {
      id: student.id,
      loginId: student.loginId,
      name: student.name,
      grade: student.grade,
      classSchedules: student.classSchedules,
      emailReceivers: student.emailReceivers,
      password: student.password,
      properties: student.properties,
      characteristic: student.characteristic,
      gmail: student.gmail ?? "",
      microsoftEmail: student.microsoftEmail ?? "",
      isPromptCreationDisabled: student.isPromptCreationDisabled ?? true,
      isStudentMessageSendingAllowed:
        student.isStudentMessageSendingAllowed ?? false
    }
  });
  if (!res.data.success) {
    let { errorMessage } = res.data as { errorMessage?: string };
    if (errorMessage) {
      switch (errorMessage) {
        case "This loginId is already used in the same school": {
          errorMessage = "すでに使われているログインIDです。";
          break;
        }

        case "This google account is already used": {
          errorMessage = "すでに使われているGoogleアカウントです。";
          break;
        }

        case "This microsoft account is already used": {
          errorMessage = "すでに使われているMicrosoftアカウントです。";
          break;
        }

        default: {
          break;
        }
      }
    }
    throw new Error(errorMessage ?? "Unexpected error occurred");
  }
}

export async function transferStudent(
  studentRef: firebase.firestore.DocumentReference,
  newClassroomId: string
) {
  const classroomRef = studentRef.parent.parent!;
  const schoolRef = classroomRef.parent.parent!;
  const callTransferStudent = firebase
    .app()
    .functions("asia-northeast1")
    .httpsCallable("transfer_student_v2");

  const res = await callTransferStudent({
    school: schoolRef.id,
    classroom: classroomRef.id,
    student: studentRef.id,
    newClassroom: newClassroomId
  });
  if (!res.data.success) {
    throw new Error("予期せぬエラーが発生しました");
  }
}

export async function deleteStudent(
  ref: firebase.firestore.DocumentReference
): Promise<void> {
  await ref.delete();
}

export async function updateStudent(
  {
    ref,
    id,
    name,
    grade,
    classSchedules,
    emailReceivers,
    password,
    studentLoginId,
    properties,
    characteristic,
    gmail,
    microsoftEmail,
    isPromptCreationDisabled,
    isStudentMessageSendingAllowed,
    polarDeviceId,
    schoolAiUsageLimits
  }: {
    ref: firebase.firestore.DocumentReference;
    id: string;
    name: string;
    grade: StudentGrade;
    classSchedules: string[];
    emailReceivers: string[];
    password: string;
    studentLoginId: string;
    properties: StudentProperty[];
    characteristic: string;
    gmail?: string;
    microsoftEmail?: string;
    isPromptCreationDisabled?: boolean;
    isStudentMessageSendingAllowed?: boolean;
    polarDeviceId?: string;
    schoolAiUsageLimits?: SchoolAiUsageLimitScope;
  },
  roleChanges: {
    loginIdEdited?: boolean;
    gmailEdited?: boolean;
    microsoftEmailEdited?: boolean;
  }
): Promise<void> {
  const { loginIdEdited, gmailEdited, microsoftEmailEdited } = roleChanges;

  const roleUpdateNeeded =
    !!loginIdEdited || !!gmailEdited || !!microsoftEmailEdited;

  if (roleUpdateNeeded) {
    // NOTE: Promise.all に並べない。先に await する(重複時の部分書き込みを防ぐ)
    const callUpdateStudentLoginInfo = firebase
      .app()
      .functions("asia-northeast1")
      .httpsCallable("update_student_login_info");
    await callUpdateStudentLoginInfo({
      studentDocPath: ref.path,
      loginId: studentLoginId,
      gmail: gmail ?? "",
      microsoftEmail: microsoftEmail ?? ""
    });
  }

  // 認証情報は上のAPIで検証・更新し、表示・ソート用のIDは同じバッチで保存する。
  // NOTE: 受け取った ref は Vuex の state 経由で Proxy に包まれていて、ref.firestore が
  // バッチの Firestore と別物と判定される。パスから同じインスタンスの ref に作り直す。
  const db = firebase.firestore();
  const studentRef = db.doc(ref.path);
  const batch = db.batch();
  batch.update(
    studentRef.parent
      .parent!.collection(studentConfigCollectionKey)
      .doc(studentRef.id),
    { password, loginId: studentLoginId }
  );
  batch.update(studentRef, {
    id,
    loginId: studentLoginId,
    name,
    grade,
    classSchedules,
    emailReceivers,
    properties,
    characteristic,
    isPromptCreationDisabled: isPromptCreationDisabled ?? true,
    isStudentMessageSendingAllowed: isStudentMessageSendingAllowed ?? false,
    polarDeviceId: polarDeviceId ?? "",
    ...(schoolAiUsageLimits ? { schoolAiUsageLimits } : {})
  });
  await batch.commit();
}

export async function updateStudentRecess(
  studentRef: firebase.firestore.DocumentReference,
  recessTime: number
) {
  const [reservations, regularlyReservations, studentData] = await Promise.all([
    getReservationsOf(studentRef),
    getRegularlyReservationsOf(studentRef),
    studentRef.get()
  ]);
  const student = convertToStudent(studentData.data()!, studentData.ref);
  await Promise.all([
    reservations.map(reservation => deleteReservation(reservation.ref)),
    regularlyReservations.map(regularlyReservation =>
      deleteRegularlyReservation(regularlyReservation.ref)
    )
  ]);
  if (student.data.isNandeUserCreated && recessTime !== 0) {
    const unlinkSchoolAi = firebase
      .app()
      .functions("asia-northeast1")
      .httpsCallable("unlink_school_ai");
    const requestPayload = {
      schoolDocId: student.ref.parent.parent!.parent.parent!.id,
      classroomDocId: student.ref.parent.parent!.id,
      studentDocId: student.ref.id,
      uidOfSchoolAI: student.data.nandeUserId
    };
    await unlinkSchoolAi({
      ...requestPayload
    });
  }
  return await studentRef.update({ recessTime });
}
const allGrades = [
  ...elementarySchoolGrades,
  ...juniorHighSchoolGrades,
  ...highSchoolGrades
] as StudentGrade[];

// 学年をシフトする関数を作成する
function generateGradeShifter(
  operand: number
): (grade: StudentGrade) => StudentGrade {
  return grade => {
    const index = allGrades.indexOf(grade);
    if (
      index === -1 ||
      index + operand < 0 ||
      index + operand > allGrades.length - 1
    )
      return allGrades[operand > 0 ? allGrades.length - 1 : 0];
    return allGrades[index + operand];
  };
}

function convertToStudentRef(
  db: firebase.firestore.Firestore,
  schoolId: string,
  classroomId: string,
  studentId: string
): firebase.firestore.DocumentReference {
  return db
    .collection(schoolCollectionKey)
    .doc(schoolId)
    .collection(classroomCollectionKey)
    .doc(classroomId)
    .collection(studentCollectionKey)
    .doc(studentId);
}

function transformStudentRefFromStudent(
  db: firebase.firestore.Firestore,
  student: Student
): firebase.firestore.DocumentReference {
  const classroomRef = student.ref.parent.parent!;
  const schoolRef = classroomRef.parent.parent!;
  return convertToStudentRef(db, schoolRef.id, classroomRef.id, student.ref.id);
}

export async function moveUpGradeOfStudents(students: Student[]) {
  const db = firebase.firestore();
  const operate = generateGradeShifter(1);
  const chunks = chunkArray(students, MAX_BATCH_SIZE);
  for (const chunk of chunks) {
    const batch = db.batch();
    chunk.forEach(student => {
      batch.update(transformStudentRefFromStudent(db, student), {
        grade: operate(student.data.grade)
      });
    });
    await batch.commit();
  }
}

export async function moveDownGradeOfStudents(students: Student[]) {
  const db = firebase.firestore();
  const operate = generateGradeShifter(-1);
  const chunks = chunkArray(students, MAX_BATCH_SIZE);
  for (const chunk of chunks) {
    const batch = db.batch();
    chunk.forEach(student => {
      batch.update(transformStudentRefFromStudent(db, student), {
        grade: operate(student.data.grade)
      });
    });
    await batch.commit();
  }
}

export function getStudentsBasedOnRole(
  isServiceProvider: boolean,
  studentsInStore: Student[],
  mySchoolDocId: string
): Student[] {
  if (!studentsInStore) {
    throw new Error("Store内に生徒データが見つかりません");
  }
  if (!mySchoolDocId) {
    throw new Error("現在ログインしているスクールのIDが見つかりません");
  }

  if (!isServiceProvider) return studentsInStore;

  return studentsInStore.filter(student => {
    const studentRef = student.ref;
    const path = studentRef.path;
    const pathSegments = path.split("/");
    const schoolId =
      pathSegments[pathSegments.indexOf(schoolCollectionKey) + 1];
    return schoolId === mySchoolDocId;
  });
}

export async function selectStudentsByRoomUsage(
  students: Student[]
): Promise<Student[]> {
  const db = firebase.firestore();

  const schoolConfigPromises = students.map(async student => {
    const schoolId = getSchoolIdOfStudent(student);
    const schoolRef = db.collection(schoolConfigCollectionKey).doc(schoolId);
    const schoolConfig = await getSchoolConfig(schoolRef);
    return { student, schoolConfig };
  });

  const results = await Promise.all(schoolConfigPromises);

  const filteredStudents = results
    .filter(({ schoolConfig }) => {
      return schoolConfig && !schoolConfig.data.useSchoolAiExclusively;
    })
    .map(({ student }) => student);

  return filteredStudents;
}
